import { lowerCase, uniqueId } from 'lodash';

import { IconDocument } from 'src/assets/icons/IconDocument';
import { IconMicrosoftDynamicsSales } from 'src/assets/icons/IconMicrosoftDynamicsSales';
import { IconSalesforce } from 'src/assets/icons/IconSalesforce';
import { MessagingBankIcon } from 'src/assets/icons/MessagingBank';
import { IconResourceImg } from 'src/assets/icons/ResourceImg';
import { IconResourceTypeAudio } from 'src/assets/icons/ResourceTypeAudio';
import { IconResourceTypeVideo } from 'src/assets/icons/ResourceTypeVideo';
import { IconResourceUrl } from 'src/assets/icons/ResourceUrl';
import { PROMPT_MENU_TYPE } from 'src/components/RichSlate';
import { EXTERNAL_CHANNEL } from 'src/data/channel';
import { EXTERNAL_CONNECTION_OBJECT_SEARCH, getResourceTagLabel } from 'src/data/performance';
import { isValidUrl } from 'src/lib/string';

export const MESSAGING_BANK_ASSET = 'Messaging bank asset';

const PROMPT_MENU_TYPE_PROPS = {
  [PROMPT_MENU_TYPE.image]: {
    label: 'Image',
  },
  [PROMPT_MENU_TYPE.url]: {
    label: 'Webpage',
  },
  [PROMPT_MENU_TYPE.bank]: {
    label: MESSAGING_BANK_ASSET,
  },
  [PROMPT_MENU_TYPE.document]: {
    label: 'File (PDF, TXT...)',
  },
  [PROMPT_MENU_TYPE.salesforce]: {
    label: 'Salesforce',
    channel: EXTERNAL_CHANNEL.salesforceCrm,
  },
  [PROMPT_MENU_TYPE.microsoftDynamicsSales]: {
    label: 'Dynamics 365 Sales',
    channel: EXTERNAL_CHANNEL.microsoftDynamicsSales,
  },
  [PROMPT_MENU_TYPE.audio]: {
    label: 'Audio',
  },
  [PROMPT_MENU_TYPE.video]: {
    label: 'Video',
  },
};

export const getPromptMenuTypeLabel = (menuType) => PROMPT_MENU_TYPE_PROPS[menuType]?.label || '';
export const getPromptMenuTypeChannel = (menuType) =>
  PROMPT_MENU_TYPE_PROPS[menuType]?.channel || '';

export const getMenuCards = ({
  hideImages,
  shouldIncludeExternalChannel,
  checkExternalChannelConnected,
  shouldIncludeAudioVideoResources,
}) => {
  const allMenuCards = [
    {
      type: PROMPT_MENU_TYPE.image,
      icon: IconResourceImg,
      label: getPromptMenuTypeLabel(PROMPT_MENU_TYPE.image),
    },
    {
      type: PROMPT_MENU_TYPE.url,
      icon: IconResourceUrl,
      label: getPromptMenuTypeLabel(PROMPT_MENU_TYPE.url),
    },
    {
      type: PROMPT_MENU_TYPE.bank,
      icon: MessagingBankIcon,
      label: getPromptMenuTypeLabel(PROMPT_MENU_TYPE.bank),
    },
    {
      type: PROMPT_MENU_TYPE.document,
      icon: IconDocument,
      label: getPromptMenuTypeLabel(PROMPT_MENU_TYPE.document),
    },
    {
      type: PROMPT_MENU_TYPE.audio,
      icon: IconResourceTypeAudio,
      label: getPromptMenuTypeLabel(PROMPT_MENU_TYPE.audio),
    },
    {
      type: PROMPT_MENU_TYPE.video,
      icon: IconResourceTypeVideo,
      label: getPromptMenuTypeLabel(PROMPT_MENU_TYPE.video),
    },
    {
      type: PROMPT_MENU_TYPE.salesforce,
      icon: IconSalesforce,
      label: getPromptMenuTypeLabel(PROMPT_MENU_TYPE.salesforce),
    },
    {
      type: PROMPT_MENU_TYPE.microsoftDynamicsSales,
      icon: IconMicrosoftDynamicsSales,
      label: getPromptMenuTypeLabel(PROMPT_MENU_TYPE.microsoftDynamicsSales),
    },
  ];

  let result = allMenuCards;

  if (hideImages) {
    result = result.filter((card) =>
      hideImages ? ![PROMPT_MENU_TYPE.image, PROMPT_MENU_TYPE.document].includes(card.type) : true
    );
  }

  if (!shouldIncludeAudioVideoResources) {
    result = result.filter(
      (card) => ![PROMPT_MENU_TYPE.audio, PROMPT_MENU_TYPE.video].includes(card.type)
    );
  }

  if (!shouldIncludeExternalChannel) {
    return result.filter(
      (card) =>
        ![PROMPT_MENU_TYPE.microsoftDynamicsSales, PROMPT_MENU_TYPE.salesforce].includes(card.type)
    );
  } else {
    if (checkExternalChannelConnected(EXTERNAL_CHANNEL.microsoftDynamicsSales)) {
      if (checkExternalChannelConnected(EXTERNAL_CHANNEL.salesforceCrm)) {
        return result;
      }
      return result.filter((card) => card.type !== PROMPT_MENU_TYPE.salesforce);
    } else {
      return result.filter((card) => card.type !== PROMPT_MENU_TYPE.microsoftDynamicsSales);
    }
  }
};

export const createDocumentResourceItem = ({ id, name, tags, file }) => ({
  id,
  label: name,
  subtitle: (tags || []).map(getResourceTagLabel)?.join(', ') || '',
  file,
  type: PROMPT_MENU_TYPE.document,
});

export const createImageResourceItem = ({ id, name, tags, file }) => ({
  id,
  label: name,
  subtitle: (tags || []).map(getResourceTagLabel)?.join(', ') || '',
  file,
  type: PROMPT_MENU_TYPE.image,
});

export const filterBrandBriefItems = (value, brandBriefsMenuItems) => {
  return brandBriefsMenuItems.filter((item) => {
    if (!value) {
      return true;
    }
    return (
      lowerCase(item.label).includes(lowerCase(value)) ||
      lowerCase(item.name).includes(lowerCase(value)) ||
      lowerCase(item.body).includes(lowerCase(value))
    );
  });
};

export const createNewUrlItem = (value, onClick) => {
  let prefix = '';
  if (!value.startsWith('http')) {
    prefix = 'https://';
  }
  if (isValidUrl(prefix + value)) {
    return {
      id: uniqueId('url'),
      type: PROMPT_MENU_TYPE.url,
      label: value,
      content: value,
      subtitle: 'URL',
      onClick,
    };
  }
  return null;
};

export const createUrlResourceItem = ({ id, name, label, tags, content }) => ({
  id,
  type: PROMPT_MENU_TYPE.url,
  label: name || label,
  content,
  subtitle: (tags || []).map(getResourceTagLabel).join(', ') || '',
});

export const createAudioResourceItem = ({ id, name, label, tags, content }) => ({
  id,
  type: PROMPT_MENU_TYPE.audio,
  label: name || label,
  content,
  subtitle: (tags || []).map(getResourceTagLabel).join(', ') || '',
});

export const createVideoResourceItem = ({ id, name, label, tags, content }) => ({
  id,
  type: PROMPT_MENU_TYPE.video,
  label: name || label,
  content,
  subtitle: (tags || []).map(getResourceTagLabel).join(', ') || '',
});

export const getExternalChannelFilterOptions = (menuType) => {
  const channel = getPromptMenuTypeChannel(menuType);

  const commonOptions = [
    EXTERNAL_CONNECTION_OBJECT_SEARCH.all,
    EXTERNAL_CONNECTION_OBJECT_SEARCH.lead,
    EXTERNAL_CONNECTION_OBJECT_SEARCH.contact,
    EXTERNAL_CONNECTION_OBJECT_SEARCH.account,
    EXTERNAL_CONNECTION_OBJECT_SEARCH.opportunity,
  ];

  if (channel === EXTERNAL_CHANNEL.salesforceCrm) {
    return commonOptions.concat(EXTERNAL_CONNECTION_OBJECT_SEARCH.case);
  } else if (channel === EXTERNAL_CHANNEL.microsoftDynamicsSales) {
    return commonOptions.concat(EXTERNAL_CONNECTION_OBJECT_SEARCH.quote);
  }
  return Object.values(EXTERNAL_CONNECTION_OBJECT_SEARCH);
};
